<template>
  <div class="flex flex-col pb-10">
    <span class="font-bold text-gray-dark text-xl">API call</span>

    <span class="font-bold text-gray-dark mt-4">Request</span>
    <div data-testid="api-request-row" class="mt-2 flex flex-col gap-4">
      <base-select v-model="requestHttpVerb"
                   :options="httpVerbOptions"
                   :use-option-slot="true"
                   data-testid="api-http-verb-select"
                   :class="[httpVerbTextClass(requestHttpVerb), 'w-32 h-10 flex-shrink-0 font-semibold']"
      >
        <template #option="{ option }">
          <span class="font-semibold" :class="httpVerbTextClass(resolveHttpVerbOptionValue(option))">
            {{ option.text }}
          </span>
        </template>
      </base-select>

      <div>
        <base-input v-model="requestUrl"
          data-testid="api-url-input"
          class="h-10"
          variant="editor-soft"
          placeholder="https://api.example.com/endpoint"
        />
        <whitelist-hint/>
      </div>
    </div>

    <base-toggle-group data-testid="api-json-tabs"
                       class="mt-4 relative z-0"
                       :tabs="requestTabOptions"
                       v-model="requestContentTab"
    />

    <json-input v-if="requestContentTab === REQUEST_CONTENT_TABS.HEADERS"
                :key="'headers-' + actionId"
                class="mt-2"
                v-model="requestHeaders"
                autofocus
    />
    <json-input v-else-if="requestContentTab === REQUEST_CONTENT_TABS.BODY"
                :key="'body-' + actionId"
                class="mt-2"
                v-model="requestBody"
                autofocus
    />
    <div v-else-if="requestContentTab === REQUEST_CONTENT_TABS.PREVIEW" class="mt-2 bg-soft border rounded p-3">
      <div class="flex items-center justify-between mb-2">
        <div v-if="hasRequestVariables" class="flex items-center">
          <base-switch :value="interpolateVariablesInCurl"
                       :key="interpolateVariablesInCurl"
                       id="apiCallCurlInterpolateVariablesToggle"
                       data-testid="api-curl-interpolate-toggle"
                       @input="interpolateVariablesInCurl = $event"
          />
          <label for="apiCallCurlInterpolateVariablesToggle"
                 data-testid="api-curl-interpolate-label"
                 class="ml-2 text-sm text-gray-dark cursor-pointer"
                 @click="interpolateVariablesInCurl = !interpolateVariablesInCurl"
          >
            interpolate variables
          </label>
        </div>
        <vx-tooltip ref="copyCurlTooltip"
                    class="flex items-center"
                    :text="curlCopied ? 'cURL command was copied to your clipboard!' : 'Copy cURL command'"
        >
          <copy-icon data-testid="api-curl-copy"
                     class="h-5 text-gray-dark hover:text-primary-focus cursor-pointer"
                     :is-copied="curlCopied"
                     @click.native="copyCurlCommand"
          />
        </vx-tooltip>
      </div>
      <pre data-testid="api-curl-preview" class="font-mono text-sm whitespace-pre-wrap break-all">{{ curlCommand }}</pre>
    </div>
    <span v-if="isErrorBubbleFocused" class="mt-1 text-xs text-focus-error">
      {{ errorDescription }}
    </span>

    <div class="mt-4 flex items-stretch gap-2">
      <base-button variant="bordered"
                 class="flex-1 justify-center"
                 :disabled="isTestingApiCall"
                 data-testid="api-test-button"
                 @click="testApiCall"
      >
        {{ testButtonText }}
      </base-button>

      <base-button v-if="hasRequestVariables"
                   variant="secondary"
                   data-testid="api-test-variables-toggle"
                   class="items-center justify-between whitespace-nowrap"
                   @click="toggleTestVariablesPanel"
      >
        <vs-icon icon="settings" class="text-gray-dark" size="1rem"/>
        <span class="mx-1">{{ testVariablesButtonText }}</span>
        <vs-icon :icon="testVariablesToggleIcon" class="text-gray-dark" size="1.2rem"/>
      </base-button>
    </div>

    <div v-if="hasRequestVariables && isTestVariablesPanelOpen"
         data-testid="api-test-variables-panel"
         class="mt-3 border rounded bg-white overflow-hidden"
    >
      <div class="px-4 py-3 border-b">
        <div class="text-gray-dark font-bold">
          Test variables
        </div>
        <span class="mt-1 block text-sm text-gray">
          Used only for testing. Saved request is not changed.
        </span>
      </div>

      <div class="px-4 py-3">
        <validated-input v-for="variableKey in variableKeys"
                         :key="variableKey"
                         :name="`apiCallTestVariable-${variableKey}`"
                         :label="variableKey"
                         :value="requestVariables[variableKey]"
                         :class="{ 'mt-3': variableKey !== variableKeys[0] }"
                         @input="updateRequestVariableValue(variableKey, $event)"
        />

      </div>
    </div>

    <span class="font-bold text-gray-dark mt-4">Response</span>
    <span>2 ways to access the response for Conditions:</span>
    <div class="flex">
      <span>•<i>${context.apiCall.body.pathToYourApi}</i></span>
      <vx-tooltip ref="pathToYourApi"
                  class="flex items-center"
                  :text="pathToYourApiCopied ? 'PathToYourApi key was copied to your clipboard!' : 'Copy pathToYourApi response key.'"
      >
        <copy-icon class="h-5 ml-1 text-gray-dark hover:text-primary-focus cursor-pointer"
                   :is-copied="pathToYourApiCopied"
                   @click.native="copyContextKey('body.pathToYourApi', 'pathToYourApi')"
        />
      </vx-tooltip>
    </div>

    <div class="flex">
      <span>•<i>${context.apiCall.httpStatus}</i></span>
      <vx-tooltip ref="httpStatus"
                  class="flex items-center"
                  :text="httpStatusCopied ? 'HttpStatus key was copied to your clipboard!' : 'Copy httpStatus response key.'"
      >
        <copy-icon class="h-5 ml-1 text-gray-dark hover:text-primary-focus cursor-pointer"
                   :is-copied="httpStatusCopied"
                   @click.native="copyContextKey('httpStatus')"
        />
      </vx-tooltip>
    </div>

    <div v-if="hasApiCallTestOutput" class="mt-4">
      <span class="font-bold text-gray-dark">Test response</span>
      <div class="bg-soft border rounded mt-2 p-3 font-mono text-sm break-all whitespace-pre-wrap">
        {{ formattedApiCallTestOutput }}
      </div>
    </div>

    <base-button variant="bordered-error" class="w-full mt-8" @click="removeApiCall">
      Remove API call
    </base-button>
  </div>
</template>

<script>
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import localStorageUtils from '@/utils/local-storage.utils'
import localStorageKeys from '@/utils/local-storage.keys'
import tailwind from '../../../tailwind.config'
import stateTreeUtils from '@/utils/state-tree.utils'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import utilsService from '@/services/amio/utils.service'
import BaseToggleGroup from '@/components/base-components/BaseToggleGroup.vue'
import BaseSwitch from '@/components/base-components/BaseSwitch.vue'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'
import JsonInput from '@/components/base-components/JsonInput.vue'
import CopyIcon from '@/assets/images/icons/CopyIcon.vue'
import WhitelistHint from '@/components/common/WhitelistHint.vue'

const HTTP_VERBS = Object.freeze(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'])
const REQUEST_CONTENT_TABS = Object.freeze({
  HEADERS: 'headers',
  BODY: 'body',
  PREVIEW: 'preview'
})
const HTTP_VERB_TEXT_CLASS = Object.freeze({
  GET: 'text-success',
  POST: 'text-warning',
  PUT: 'text-focus',
  PATCH: 'text-primary',
  DELETE: 'text-focus-error',
  HEAD: 'text-gray',
  OPTIONS: 'text-gray-dark'
})

export default {
  name: 'ApiCallEditor',

  components: { BaseToggleGroup, BaseSwitch, ValidatedInput, JsonInput, CopyIcon, WhitelistHint },

  watch: {
    actionId: {
      immediate: true,
      handler() {
        this.syncRequestVariablesWithPlaceholders()
      }
    },
    variableKeys: {
      immediate: true,
      handler() {
        this.syncRequestVariablesWithPlaceholders()
      }
    }
  },

  data() {
    return {
      REQUEST_CONTENT_TABS,
      pathToYourApiCopied: false,
      httpStatusCopied: false,
      curlCopied: false,
      interpolateVariablesInCurl: false,
      isTestVariablesPanelOpen: false,
      isTestingApiCall: false,
      apiCallTestResponse: null,
      apiCallTestError: null,
      requestContentTab: REQUEST_CONTENT_TABS.HEADERS,
      requestVariables: {}
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    stateTree: chatBubbleModule.stateTree,
    template: templateModule.template,
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isErrorBubbleFocused() {
      return chatBubbleModule.isErrorBubbleFocused()
    },

    focusColor() {
      return tailwind.theme.colors.focus
    },

    stateId() {
      return this.bubbleSelected.stateId
    },

    actionId() {
      return this.bubbleSelected.actionId
    },

    conversationType() {
      return conversationModule.getType()
    },

    conversationId() {
      return conversationModule.getId()
    },

    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    httpVerbOptions() {
      return HTTP_VERBS.map(httpVerb => ({ text: httpVerb, value: httpVerb }))
    },

    requestTabOptions() {
      return [
        { key: REQUEST_CONTENT_TABS.HEADERS, label: 'Headers' },
        { key: REQUEST_CONTENT_TABS.BODY, label: 'Body' },
        { key: REQUEST_CONTENT_TABS.PREVIEW, label: 'cURL' }
      ]
    },

    requestUrl: {
      get() {
        return this.action?.url || ''
      },

      set(url) {
        this.updateRemoteActionModel({ url })
      }
    },

    requestHttpVerb: {
      get() {
        return this.normalizeHttpVerb(this.action?.httpVerb)
      },

      set(httpVerb) {
        this.updateRemoteActionModel({ httpVerb })
      }
    },

    requestHeaders: {
      get() {
        return this.normalizeJsonObject(this.action?.headers)
      },

      set(headers) {
        this.updateRemoteActionModel({ headers })
      }
    },

    requestBody: {
      get() {
        return this.normalizeJsonObject(this.action?.body)
      },

      set(body) {
        this.updateRemoteActionModel({ body })
      }
    },

    variableKeys() {
      return this.extractVariableKeys()
    },

    variablesStorageKey() {
      return `${this.conversationType}:${this.conversationId}:${this.actionId}`
    },

    requestVariablesJson: {
      get() {
        return this.requestVariables
      },
      set(value) {
        this.updateRequestVariables(value)
      }
    },

    hasApiCallTestOutput() {
      return this.apiCallTestResponse !== null || this.apiCallTestError !== null
    },

    formattedApiCallTestOutput() {
      const output = this.apiCallTestError || this.apiCallTestResponse
      if (!output) return ''

      try {
        return JSON.stringify(output, null, 2)
      } catch (error) {
        return String(output)
      }
    },

    testButtonText() {
      return this.isTestingApiCall ? 'Testing...' : 'Test API call'
    },

    hasRequestVariables() {
      return this.variableKeys.length > 0
    },

    testVariablesButtonText() {
      return `Test variables (${this.variableKeys.length})`
    },

    testVariablesToggleIcon() {
      return this.isTestVariablesPanelOpen ? 'arrow_drop_up' : 'arrow_drop_down'
    },

    curlCommand() {
      const curlVariables = this.requestVariables
      const resolvedUrl = this.interpolateVariablesInCurl ?
        this.applyTestVariables(this.requestUrl, curlVariables) :
        this.requestUrl
      const resolvedHeaders = this.interpolateVariablesInCurl ?
        this.applyTestVariables(this.requestHeaders, curlVariables) :
        this.requestHeaders
      const resolvedBody = this.interpolateVariablesInCurl ?
        this.applyTestVariables(this.requestBody, curlVariables) :
        this.requestBody

      return this.buildCurlCommand({
        url: resolvedUrl,
        httpVerb: this.requestHttpVerb,
        headers: resolvedHeaders,
        body: resolvedBody
      })
    }
  },

  methods: {
    removeApiCall() {
      const node = stateTreeUtils.findNode(this.stateTree, this.bubbleSelected)
      this.previewController.removeBubble(node, this.template?.id)
    },

    updateRemoteAction(json) {
      conversationModule.updateRemoteAction(this.actionId, json)
    },

    clearApiCallTestOutput() {
      this.apiCallTestResponse = null
      this.apiCallTestError = null
    },

    normalizeJsonObject(value) {
      const normalizedValue = value && typeof value === 'object' && !Array.isArray(value) ?
        JSON.parse(JSON.stringify(value)) :
        {}

      return normalizedValue
    },

    getStoredRequestVariables() {
      return localStorageUtils.getSegmentValue(localStorageKeys.API_CALL_TEST_VARIABLES, this.variablesStorageKey) || {}
    },

    persistRequestVariables(variables = this.requestVariables) {
      localStorageUtils.updateSegmentValue(localStorageKeys.API_CALL_TEST_VARIABLES, this.variablesStorageKey, variables)
    },

    extractVariableKeysFromValue(value, variableKeys = new Set()) {
      if (typeof value === 'string') {
        const matches = value.matchAll(/\$\{([^}]+)\}/g)
        for (const match of matches) {
          if (match[1]) variableKeys.add(match[1])
        }
        return variableKeys
      }

      if (Array.isArray(value)) {
        value.forEach(item => this.extractVariableKeysFromValue(item, variableKeys))
        return variableKeys
      }

      if (value && typeof value === 'object') {
        Object.values(value).forEach(item => this.extractVariableKeysFromValue(item, variableKeys))
      }

      return variableKeys
    },

    extractVariableKeys() {
      const variableKeys = new Set()
      this.extractVariableKeysFromValue(this.requestUrl, variableKeys)
      this.extractVariableKeysFromValue(this.requestHeaders, variableKeys)
      this.extractVariableKeysFromValue(this.requestBody, variableKeys)
      return Array.from(variableKeys).sort()
    },

    normalizeVariablesObject(value) {
      const normalizedVariables = this.normalizeJsonObject(value)
      return this.variableKeys.reduce((variables, variableKey) => {
        variables[variableKey] = Object.prototype.hasOwnProperty.call(normalizedVariables, variableKey) ?
          normalizedVariables[variableKey] :
          ''
        return variables
      }, {})
    },

    updateRequestVariables(variables) {
      this.requestVariables = this.normalizeVariablesObject(variables)
      this.persistRequestVariables()
    },

    updateRequestVariableValue(variableKey, value) {
      this.updateRequestVariables({
        ...this.requestVariables,
        [variableKey]: value
      })
    },

    syncRequestVariablesWithPlaceholders() {
      const storedVariables = this.getStoredRequestVariables()
      const nextVariables = this.variableKeys.reduce((variables, variableKey) => {
        if (Object.prototype.hasOwnProperty.call(this.requestVariables, variableKey)) {
          variables[variableKey] = this.requestVariables[variableKey]
          return variables
        }

        if (Object.prototype.hasOwnProperty.call(storedVariables, variableKey)) {
          variables[variableKey] = storedVariables[variableKey]
          return variables
        }

        variables[variableKey] = ''
        return variables
      }, {})

      this.requestVariables = nextVariables
      this.persistRequestVariables(nextVariables)
    },

    toggleTestVariablesPanel() {
      this.isTestVariablesPanelOpen = !this.isTestVariablesPanelOpen
    },

    substituteVariablesInString(value, variables) {
      const exactVariableMatch = value.match(/^\$\{([^}]+)\}$/)
      if (exactVariableMatch && Object.prototype.hasOwnProperty.call(variables, exactVariableMatch[1])) {
        return variables[exactVariableMatch[1]]
      }

      return value.replace(/\$\{([^}]+)\}/g, (placeholder, variableKey) => {
        if (!Object.prototype.hasOwnProperty.call(variables, variableKey)) return placeholder
        return String(variables[variableKey])
      })
    },

    applyTestVariables(value, variables) {
      if (typeof value === 'string') {
        return this.substituteVariablesInString(value, variables)
      }

      if (Array.isArray(value)) {
        return value.map(item => this.applyTestVariables(item, variables))
      }

      if (value && typeof value === 'object') {
        return Object.entries(value).reduce((substituted, [key, nestedValue]) => {
          substituted[key] = this.applyTestVariables(nestedValue, variables)
          return substituted
        }, {})
      }

      return value
    },

    buildRemoteAction({
      url = this.requestUrl,
      httpVerb = this.requestHttpVerb,
      body = this.requestBody,
      headers = this.requestHeaders
    } = {}) {
      return {
        type: 'remote',
        url: url || '',
        httpVerb: this.normalizeHttpVerb(httpVerb),
        body: this.normalizeJsonObject(body),
        headers: this.normalizeJsonObject(headers)
      }
    },

    formatCurlHeaderValue(value) {
      if (value === null || typeof value === 'undefined') return ''
      return typeof value === 'string' ? value : JSON.stringify(value)
    },

    quoteCurlArgument(value) {
      return "'" + String(value).replace(/'/g, "'\"'\"'") + "'"
    },

    buildCurlCommand({
      url = this.requestUrl,
      httpVerb = this.requestHttpVerb,
      body = this.requestBody,
      headers = this.requestHeaders
    } = {}) {
      const normalizedHttpVerb = this.normalizeHttpVerb(httpVerb)
      const normalizedUrl = (url || '').trim()
      const normalizedHeaders = this.normalizeJsonObject(headers)
      const normalizedBody = this.normalizeJsonObject(body)
      const curlLines = [`curl --request ${normalizedHttpVerb}`]

      if (normalizedUrl) {
        curlLines.push(`--url ${this.quoteCurlArgument(normalizedUrl)}`)
      }

      Object.entries(normalizedHeaders).forEach(([key, value]) => {
        curlLines.push(`--header ${this.quoteCurlArgument(`${key}: ${this.formatCurlHeaderValue(value)}`)}`)
      })

      if (this.shouldSendRequestBody(normalizedHttpVerb)) {
        curlLines.push(`--data ${this.quoteCurlArgument(JSON.stringify(normalizedBody, null, 2))}`)
      }

      return curlLines.join(' \\\n  ')
    },

    updateRemoteActionModel({
      url = this.requestUrl,
      httpVerb = this.requestHttpVerb,
      body = this.requestBody,
      headers = this.requestHeaders
    }) {
      const outgoingAction = this.buildRemoteAction({ url, httpVerb, body, headers })
      this.updateRemoteAction({
        ...outgoingAction
      })
      this.clearApiCallTestOutput()
      this.error = null
    },

    resolveHttpVerbOptionValue(option) {
      return option?.raw?.value || option?.value || option?.text || 'POST'
    },

    normalizeHttpVerb(httpVerb) {
      if (typeof httpVerb === 'string') return (httpVerb || 'POST').toUpperCase()
      const optionHttpVerb = this.resolveHttpVerbOptionValue(httpVerb)
      return (optionHttpVerb || 'POST').toUpperCase()
    },

    httpVerbTextClass(httpVerb) {
      return HTTP_VERB_TEXT_CLASS[httpVerb] || 'text-gray-dark'
    },

    shouldSendRequestBody(httpVerb) {
      return !['get', 'head'].includes((httpVerb || '').toLowerCase())
    },

    mapApiCallTestError(error) {
      return {
        message: error?.message || 'Request failed',
        httpStatus: error?.response?.status || null,
        body: error?.response?.data || null,
        headers: error?.response?.headers || null
      }
    },

    async testApiCall() {
      const url = this.requestUrl?.trim()
      const httpVerb = this.requestHttpVerb?.trim()

      this.clearApiCallTestOutput()

      if (!url || !httpVerb) {
        this.apiCallTestError = {
          message: 'Both url and httpVerb are required before testing this API call.',
          httpStatus: null,
          body: null,
          headers: null
        }
        return
      }

      this.isTestingApiCall = true
      try {
        const response = await utilsService.testApiCall({
          url,
          httpVerb,
          headers: this.requestHeaders,
          body: this.shouldSendRequestBody(httpVerb) ? this.requestBody : undefined,
          variables: this.requestVariables
        })

        if (response.error) {
          this.apiCallTestError = response
        } else {
          this.apiCallTestResponse = response
        }
      } catch (error) {
        this.apiCallTestError = this.mapApiCallTestError(error)
      } finally {
        this.isTestingApiCall = false
      }
    },

    async copyContextKey(key, tooltip = 'httpStatus') {
      this[`${tooltip}Copied`] = true
      await this.$copyText('${context.apiCall.' + key + '}')
      this.$refs[tooltip]?.mouseenterx()
      await this.sleep(2000)
      this[`${tooltip}Copied`] = false
      this.$refs[tooltip]?.mouseleavex()
    },

    async copyCurlCommand() {
      this.curlCopied = true
      await this.$copyText(this.curlCommand)
      this.$refs.copyCurlTooltip?.mouseenterx()
      await this.sleep(2000)
      this.curlCopied = false
      this.$refs.copyCurlTooltip?.mouseleavex()
    }
  }
}
</script>
